import {
  PapiDamzRoomAddParticipants,
  PapiDamzRoomCreate,
  PapiDamzRoomKick,
  PapiRequestDamzRoomAddParticipants,
  PapiRequestDamzRoomCreate,
  PapiRequestDamzRoomKick,
  PapiUser,
} from '@wix/da-papi-types';
import ddt from '@wix/da-ddt';
import {
  all,
  call,
  delay,
  fork,
  getContext,
  put,
  select,
  takeEvery,
  takeLatest,
  cancel,
  take,
} from 'redux-saga/effects';
import { eventChannel } from 'redux-saga';
import { createFormData, isApiError, requestPuppy, XhrResponse } from '@wix/da-http-client';
import { deleteStream, initializeIfNeeded } from '@wix/da-shared-react/pkg/Stream/base/actions';
import { StreamType } from '@wix/da-shared-react/pkg/Stream';
import {
  cancelEditMessage,
  closeRoom,
  fetchMessageNext,
  fetchMessagePrev,
  markRoomRead,
  markRoomReadSyncDone,
  markAsRead,
  markAsUnread,
  reloadActiveRoom,
  roomChannelReconnected,
  selectRoom,
  setActiveRoomData,
  stopTyping,
  updateDrawerRoomFlagState,
  createCommunityChannel,
  createRoom,
  fetchActiveParticipants,
  fetchActiveParticipantsSuccess,
  addParticipants,
  kickParticipant,
  fetchRoomUnreadState,
  clearSendError,
  selectOrCreateRoom,
  closeRoomParticipantsList,
  openRoomParticipantsList,
  toggleStartNewMessage,
  requestFocusOnMessage,
  closeNewChat,
  selectRoomAndOpenParticipantsList,
} from '../../actions/room';
import { SupabaseClient } from '@supabase/supabase-js';
import { Database } from '@wix/da-papi-types/lib/supabase';
import { addErrorNotification } from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import i18next from 'i18next';
import { getRoomStreamId, roomFetchToRoomItem, fetchRoomById, makePapiUser } from '../../helpers';
import {
  getActiveRoomId,
  getJustClosedRoomId,
  getLastActiveRoom,
  getMarkAsReadQueue,
} from '../../selectors/room';
import { getCurrentUser } from '@wix/da-shared-react/pkg/publicSession/selectors';
import { getItems, getStream, isInitialized } from '@wix/da-shared-react/pkg/Stream/base/selectors';
import {
  drawerStreamId,
  FETCH_LIMIT_MESSAGES_DEFAULT,
  roomStreamGroupId,
  TEMP_DRAFT_ROOM_ID,
} from '../../../constants';
import { RoomFlagNames, RoomFlags, RoomType } from '../../../types';
import { getConfigKey } from '@wix/da-redux-config/pkg/selectors';
import history from '@wix/da-shared-react/pkg/redux/routing/history';
import { listenToRoomChannel } from './room-realtime';
import {
  decrementGlobalCounter,
  delayedRefreshCounter,
  incrementGlobalCounter,
  refreshCounter,
} from '../../actions/counter';
import { historyChangedDamz } from '@wix/da-shared-react/pkg/redux/routing/actions';
import { isOverview, isRoomView, parseRoomIdFromPath } from '../../../utils/url-parsing';
import { toggleDamzPage } from '@wix/da-shared-react/pkg/SiteHeader/redux/actions';
import { getIsDamzClosed } from '../../selectors/app';
import { getDrawerActiveFilter } from '../../selectors/drawer';
import { initDrawer, reloadDrawerStream } from '../../actions/drawer';
import { MODAL_TYPES } from '../../../project-tools';
import { pushModal } from '@wix/da-shared-react/pkg/Modals/redux/basicActions';
import { getChatters, getErrorTextFromResponse } from '../helpers';
import uniq from 'lodash/uniq';
import { addDraftUser, removeDraftUserAll } from '../../actions/drafts';
import { realtimeConnections } from '../../RealtimeConnections';

const logger = ddt.createLogger('damz');

function* handleCreateCommunityChannel({ payload }) {
  const { name, userUuids } = payload;
  const currentUser: PapiUser = yield select(getCurrentUser);
  yield call(createRoomXhr, {
    roomType: 'COMMUNITY',
    userUuids: [currentUser.useridUuid, ...userUuids],
    name,
  });
  // If damz is closed, open it
  const isDamzClosed = yield select(getIsDamzClosed);
  if (isDamzClosed) {
    yield put(toggleDamzPage());
  }
}

function* handleCreateRoom({ payload }) {
  // wrapper to un-flux the createRoomXhr params
  yield call(createRoomXhr, payload);

  // If damz is closed, open it
  const isDamzClosed = yield select(getIsDamzClosed);
  if (isDamzClosed) {
    yield put(toggleDamzPage());
  }
}

function* createRoomXhr({
  roomType,
  userUuids,
  name,
}: {
  roomType: RoomType;
  userUuids: string[];
  name?: string;
}) {
  const formData = createFormData<PapiRequestDamzRoomCreate>({
    type: roomType.toLowerCase(),
    participant_ids: userUuids,
    ...(!!name && { name }),
  });

  const response: XhrResponse<PapiDamzRoomCreate> = yield call(
    requestPuppy,
    {
      method: 'post',
      url: 'room/create',
      data: formData,
    },
    undefined,
    'damz'
  );

  if (!response || isApiError(response) || !response.roomId) {
    const errorText = getErrorTextFromResponse('createRoom', response);
    const params = { roomType, userUuids, name };
    logger.error('error creating room:', errorText, params);
    yield put(addErrorNotification(errorText));
    return;
  }

  // reload the roomlist and select the new room
  const activeFilter = yield select(getDrawerActiveFilter);
  const streamId = `${drawerStreamId}${activeFilter}`;
  yield put(deleteStream(streamId));
  yield put(initDrawer(activeFilter));

  yield put(selectRoom(response.roomId, undefined, false, false));

  return response.roomId;
}

function* fetchRoom(room_id: string) {
  const doFake = yield select(getConfigKey, 'fakeApiRequests');

  if (doFake && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line
    const Chance = require('chance').Chance;
    const chance = new Chance();
    const fakeRoomData = {
      room_id: '1',
      name: chance.word(),
      room_type: 1,
      updated_at: chance.timestamp(),
      owner_id: chance.guid(),
      user_count: 3,
      flags: 0,
      dm_username: chance.name(),
      dm_user_id: chance.guid(),
      dm_usericon: chance.integer({ min: 1, max: 100 }),
      dm_symbol: chance.string({ length: 1 }),
      dm_user_banned: false,
      msg: chance.sentence(),
      msg_username: chance.name(),
      msg_user_id: chance.guid(),
      msg_usericon: chance.integer({ min: 1, max: 100 }),
      msg_symbol: chance.string({ length: 1 }),
      msg_user_banned: false,
      multi_username: '',
      multi_icon: '',
    } as Database['damz']['Functions']['get_conversations_by_room_id']['Returns'][0];

    yield put(setActiveRoomData(roomFetchToRoomItem([fakeRoomData])[0]));
    return true;
  }

  const roomData = yield fetchRoomById(room_id);
  if (!roomData) {
    yield put(addErrorNotification(i18next.t('damz:damz.room.fetch.error')));
    return false;
  }

  return roomFetchToRoomItem([roomData])[0];
}

function* handleSelectRoom(action: ReturnType<typeof selectRoom>) {
  const { room_id, messageId } = action.payload;

  yield put(stopTyping()); // clearing typing indicator when switching rooms
  yield delay(100);

  // First fetch the room
  const room = yield fetchRoom(room_id);
  if (!room) {
    return;
  }

  yield put(closeNewChat());
  yield put(clearSendError());

  // If the room is banned and we're not forcing open anyway, show the warning modal
  if (!action.payload.forceOpenAnyway && room.dmUser?.isBanned) {
    yield put(closeRoom());
    yield put(
      pushModal(MODAL_TYPES.BANNED_USER_MODAL, {
        params: {
          room_id,
          bannedUser: room.dmUser,
        },
      })
    );
    return;
  }

  // Clear the previous stream so we don't have many rooms in memory
  const prevSelected = yield select(getLastActiveRoom);
  const roomChannelTasks = yield getContext('roomChannelTasks');
  if (prevSelected && prevSelected !== room_id) {
    // Cancel the previous room's listenToRoomChannel task if it exists
    if (roomChannelTasks.has(prevSelected)) {
      yield cancel(roomChannelTasks.get(prevSelected));
      roomChannelTasks.delete(prevSelected);
    }
    yield put(deleteStream(getRoomStreamId(prevSelected)));
  }

  yield put(setActiveRoomData(room));
  if (isRoomView(history.location.pathname)) {
    history.replace(`/messages/${room_id}`);
  } else {
    history.push(`/messages/${room_id}`);
  }

  // When we select a room it becomes read
  // skipDecrement is used when accepting a request, since request rooms aren't counted in conversations
  yield put(markRoomRead(room_id, action.payload.skipDecrement));

  // Fetch the room unread state (this is message level one)
  yield put(fetchRoomUnreadState(room_id));

  // Next setup the stream
  const streamId = getRoomStreamId(room_id);
  const isStreamInitialized = yield select(isInitialized, streamId);
  if (isStreamInitialized && messageId) {
    // normally we do this in the onSuccess of the message fetching but,
    // if this room is already active, we already have those - so we need to do it here
    yield put(requestFocusOnMessage(messageId, true));
  }

  yield put(
    initializeIfNeeded(streamId, {
      streamType: StreamType.WITH_CURSOR,
      autoFetch: true,
      streamParams: {
        room_id,
        message_id: messageId,
      },
      itemsPerFetch: FETCH_LIMIT_MESSAGES_DEFAULT,
      windowSize: Infinity, // TODO: this was bugging out long lists as it would cap the window size at ~30k pixels
      hasMore: true,
      hasLess: true,
      fetchNextCallback: fetchMessageNext,
      fetchPreviousCallback: fetchMessagePrev,
      groupId: roomStreamGroupId,
    })
  );

  // Validate room_id before forking
  if (!room_id) {
    ddt.log('damz', 'Cannot fork listenToRoomChannel - room_id is undefined');
    return;
  }

  // Idempotent forking of listenToRoomChannel, you can only sub once per topic!
  const existingTask = roomChannelTasks.get(room_id);
  if (!existingTask || existingTask.isCancelled()) {
    const task = yield fork(listenToRoomChannel, room_id);
    roomChannelTasks.set(room_id, task);
  } else {
    ddt.log('damz', 'listenToRoomChannel already forked, skipping', room_id);
  }
}

function* handleSelectOrCreateRoom(action: ReturnType<typeof selectOrCreateRoom>) {
  const { user, room_id } = action.payload;

  // First fetch the room
  if (room_id) {
    try {
      const room = yield fetchRoomById(room_id);
      if (room) {
        // found one: select it
        yield put(selectRoom(room_id, undefined, false, false));
        // todo: there's an edge case here where a search will
        // return a room that's not in the sidebar's room list yet
        // (not scrolled enough) - this may cause errors
        return;
      }
    } catch (error) {
      logger.error(error);
    }
  }

  // we couldn't get the room for some reason, fall thru to
  // creating a new chat below
  yield put(closeRoomParticipantsList());
  yield put(toggleStartNewMessage());
  yield put(removeDraftUserAll(TEMP_DRAFT_ROOM_ID));
  yield put(addDraftUser(user, TEMP_DRAFT_ROOM_ID));
}

function* handleCloseRoom(action: ReturnType<typeof closeRoom>) {
  const activeRoomId: ReturnType<typeof getJustClosedRoomId> = yield select(getJustClosedRoomId);

  // Cancel the listenToRoomChannel task for this room if it exists
  const roomChannelTasks = yield getContext('roomChannelTasks');
  if (activeRoomId && roomChannelTasks.has(activeRoomId)) {
    yield cancel(roomChannelTasks.get(activeRoomId));
    roomChannelTasks.delete(activeRoomId);
  }

  if (activeRoomId) {
    yield put(deleteStream(getRoomStreamId(activeRoomId)));
    yield put(stopTyping());
    yield put(cancelEditMessage(activeRoomId));
  }

  // Navigate back to messages overview (no specific room)
  history.replace('/messages');
}

function* handleReloadActiveRoom(action: ReturnType<typeof reloadActiveRoom>) {
  const activeRoomId: ReturnType<typeof getActiveRoomId> = yield select(getActiveRoomId);
  if (activeRoomId) {
    yield put(deleteStream(getRoomStreamId(activeRoomId)));
    yield put(selectRoom(activeRoomId, undefined, false, false));
  }
}

function* handleRoomChannelReconnected(action: ReturnType<typeof roomChannelReconnected>) {
  const activeRoomId: ReturnType<typeof getActiveRoomId> = yield select(getActiveRoomId);
  if (activeRoomId) {
    ddt.log('damz', 'Room channel reconnected after timeout, reloading active room', activeRoomId);
    yield put(reloadActiveRoom());
  }
  yield put(refreshCounter());
  yield put(reloadDrawerStream());
}

export function* getActiveRoomStream() {
  const activeRoomId: ReturnType<typeof getActiveRoomId> = yield select(getActiveRoomId);
  if (!activeRoomId) {
    return;
  }

  return (yield select(getStream, activeRoomId)) as ReturnType<typeof getStream>;
}

/**
 * We queue read marking requests for 1000ms, this is used with takeLatest
 * so will gather all quickly fired events and flush last one once 500ms ends.
 */
function* handleMarkRoomReadSync() {
  yield delay(1000);

  let queueItems: { room_id: string; skipDecrement: boolean }[] = yield select(getMarkAsReadQueue);

  const client: SupabaseClient<Database, 'damz'> = yield getContext('supaClient');

  // Filter out rooms that isnt marked as unread
  const activeFilter = yield select(getDrawerActiveFilter);
  const streamId = `${drawerStreamId}${activeFilter}`;
  const items = yield select(getItems, streamId);
  const itemsByRoomId = new Map<string, (typeof items)[0]>();
  for (const item of items) {
    if (item?.room?.room_id) {
      itemsByRoomId.set(item.room.room_id, item);
    }
  }
  queueItems = queueItems.filter(queueItem => {
    const item = itemsByRoomId.get(queueItem.room_id);
    if (!item) {
      return true; // If we don't find the item, assume it's unread
    }
    return item && item.roomMeta.flags & RoomFlags.unread;
  });

  if (queueItems.length === 0) {
    yield put(markRoomReadSyncDone());
    return;
  }

  const room_ids = queueItems.map(item => item.room_id);

  let response;
  try {
    response = yield client.rpc('batch_set_room_participant_flag', {
      p_room_ids: uniq(room_ids),
      p_bit: RoomFlags.unread,
      p_onoff: false,
    });
  } catch (err) {
    ddt.error('damz', 'batch_set_room_participant_flag failed', err);
    return;
  }

  if (!response || isApiError(response)) {
    ddt.alert(
      'damz',
      '[damz] Failed to batch_set_room_participant_flag',
      ['damz', 'db'],
      response.error
    );
    return;
  }

  // Clear queue
  yield put(markRoomReadSyncDone());

  // Update on the client
  yield put(updateDrawerRoomFlagState(room_ids, 'unread', false));

  // Only decrement counter for rooms that weren't requests (skipDecrement = false).
  // Request rooms are not counted in the conversations counter, so decrementing would be incorrect.
  // Same room can appear multiple times (e.g. user opened request room then accepted: first
  // markRoomRead(room, false), then markRoomRead(room, true)); use last occurrence per room so
  // accept's skipDecrement: true wins and we don't incorrectly decrement.
  const skipDecrementByRoom = new Map<string, boolean>();
  for (const item of queueItems) {
    skipDecrementByRoom.set(item.room_id, item.skipDecrement);
  }
  const roomsToDecrement = uniq(room_ids).filter(room_id => !skipDecrementByRoom.get(room_id));
  if (roomsToDecrement.length > 0) {
    yield put(decrementGlobalCounter(roomsToDecrement));
  }

  // Schedule a server sync, hopefully make us consistent and avoid replication lag
  yield put(delayedRefreshCounter(10_000));
}

export type SettableRoomFlagNames = Extract<
  RoomFlagNames,
  'muted' | 'unread' | 'archived' | 'starred' | 'request'
>;

function* handleSetRoomFlag(room_id: string, whichFlag: SettableRoomFlagNames, newValue: boolean) {
  const client: SupabaseClient<Database, 'damz'> = yield getContext('supaClient');
  let response;
  try {
    response = yield client.rpc('set_room_participant_flag', {
      p_room_id: room_id,
      p_bit: RoomFlags[whichFlag],
      p_onoff: newValue,
    });
  } catch (err) {
    ddt.error('damz', 'set_room_participant_flag failed', err);
    return;
  }

  if (!response || isApiError(response)) {
    ddt.alert('damz', '[damz] Failed to set_room_participant_flag', ['damz', 'db'], response.error);
    const { code, message } = response?.error || {};
    yield put(
      addErrorNotification(
        code && message
          ? i18next.t('damz:damz.room.flags.set.error', { code, message })
          : i18next.t('damz:damz.room.flags.set.error.generic')
      )
    );
  }
  return response;
}

function* handleMarkAsRead(action: ReturnType<typeof markAsRead>) {
  const { room_id } = action.payload;
  yield handleSetRoomFlag(room_id, 'unread', false);
  yield put(updateDrawerRoomFlagState(room_id, 'unread', false));
  yield put(decrementGlobalCounter(room_id));
  yield put(delayedRefreshCounter(10_000));
}

function* handleMarkAsUnread(action: ReturnType<typeof markAsUnread>) {
  const { room_id } = action.payload;
  yield handleSetRoomFlag(room_id, 'unread', true);
  yield put(updateDrawerRoomFlagState(room_id, 'unread', true));
  yield put(incrementGlobalCounter(room_id));
  yield put(delayedRefreshCounter(10_000));
}

/**
 * Handle history changes for damz.
 *
 * This doesn't provide full history navigation as we use replace state not push state.
 * It does provide slightly better UX though.
 */
function* handleHistoryChanged(action: ReturnType<typeof historyChangedDamz>) {
  const isDamzClosed = yield select(getIsDamzClosed);
  const currentActiveRoomId: ReturnType<typeof getActiveRoomId> = yield select(getActiveRoomId);

  const { pathname } = location;
  ddt.log('damz', 'History changed', { pathname, action, currentActiveRoomId });

  // Parse room ID from new URL
  const newRoomId = parseRoomIdFromPath(pathname);
  if (isRoomView(pathname) && newRoomId) {
    if (!currentActiveRoomId || currentActiveRoomId !== newRoomId) {
      ddt.log('damz', 'Selecting room from history', newRoomId);
      yield put(selectRoom(newRoomId, undefined, false, false));
    }
  }

  // Handle overview (no room selected)
  if (isOverview(pathname)) {
    if (currentActiveRoomId) {
      ddt.log('damz', 'Closing room from history');
      yield put(closeRoom());
    }
    if (isDamzClosed) {
      yield put(toggleDamzPage());
    }
  }
}

function* handleFetchActiveParticipants(action: ReturnType<typeof fetchActiveParticipants>) {
  const room_id = action.payload.room_id;

  const doFake = yield select(getConfigKey, 'fakeApiRequests');
  if (doFake && process.env.NODE_ENV !== 'production') {
    const currentUser = yield select(getCurrentUser);
    // eslint-disable-next-line global-require
    const Chance = require('chance').Chance;
    const chance = new Chance();

    // eslint-disable-next-line global-require
    const users = require('@wix/da-sample-data').users;
    const participants = [currentUser, ...chance.pickset(3, users)];
    yield put(fetchActiveParticipantsSuccess(participants));
    return;
  }

  const client: SupabaseClient<Database, 'damz'> = yield getContext('supaClient');
  let response;
  try {
    response = yield client.rpc('get_room_participants', {
      p_room_id: room_id,
    });
  } catch (err) {
    logger.error('error fetching participants:', err);
    yield put(addErrorNotification(i18next.t('damz:damz.room.participants.error')));
    return;
  }

  if (response?.data) {
    const participants: PapiUser[] = response.data.map(p =>
      makePapiUser(p.username, p.uuid, p.user_id_int, p.usericon, p.symbol, p.banned_left)
    );
    yield put(fetchActiveParticipantsSuccess(participants));
  } else {
    const errorText = getErrorTextFromResponse('fetchActiveParticipants', response);
    const params = { room_id };
    logger.error('error fetching participants:', errorText, params);
    yield put(addErrorNotification(errorText));
    return;
  }
}

function* handleAddParticipants(action: ReturnType<typeof addParticipants>) {
  const { room_id, participants } = action.payload;

  const doFake = yield select(getConfigKey, 'fakeApiRequests');
  if (doFake && process.env.NODE_ENV !== 'production') {
    // dont check, just add them
    yield put(fetchActiveParticipantsSuccess(participants));
    return;
  }

  const formData = createFormData<PapiRequestDamzRoomAddParticipants>({
    room_id,
    participant_ids: participants.map(p => p.useridUuid),
    chatters: yield call(getChatters, room_id),
  });

  const response: XhrResponse<PapiDamzRoomAddParticipants> = yield call(
    requestPuppy,
    {
      method: 'post',
      url: 'room/add_participants',
      data: formData,
    },
    undefined,
    'damz'
  );

  if (!response || isApiError(response)) {
    const errorText = getErrorTextFromResponse('addParticipants', response);
    const params = { room_id };
    logger.error('error adding participants:', errorText, params);
    yield put(addErrorNotification(errorText));
    return;
  } else if (response.success) {
    if (response.skippedUsers && response.skippedUsers.length > 0) {
      yield put(
        addErrorNotification(
          i18next.t('damz:damz.room.addParticipants.skippedUsers', {
            skippedUsers: response.skippedUsers.join(', '),
          })
        )
      );
    }
    // refetch is safer but less efficient
    yield put(fetchActiveParticipants(room_id));
  }
}

function* handleKickParticipant(action: ReturnType<typeof kickParticipant>) {
  const { room_id, participant } = action.payload;

  const doFake = yield select(getConfigKey, 'fakeApiRequests');
  if (doFake && process.env.NODE_ENV !== 'production') {
    return;
  }

  const formData = createFormData<PapiRequestDamzRoomKick>({
    room_id,
    user_id: participant.useridUuid,
    chatters: yield call(getChatters, room_id),
  });

  const response: XhrResponse<PapiDamzRoomKick> = yield call(
    requestPuppy,
    {
      method: 'post',
      url: 'room/kick',
      data: formData,
    },
    undefined,
    'damz'
  );

  if (!response || isApiError(response)) {
    const errorText = getErrorTextFromResponse('kickParticipant', response);
    const params = { room_id, participant };
    logger.error('error kicking participant:', errorText, params);
    yield put(addErrorNotification(errorText));
    return;
  } else if (response.success) {
    yield put(fetchActiveParticipants(room_id));
  } else {
    logger.error('kicking participant denied:', { room_id });
  }
}

function* handleSelectRoomAndOpenParticipantsList(
  action: ReturnType<typeof selectRoomAndOpenParticipantsList>
) {
  const { room_id } = action.payload;

  // if we're requesting different from active, we need to select first
  const activeRoomId = yield select(getActiveRoomId);
  if (activeRoomId !== room_id) {
    yield put(selectRoom(room_id, undefined, true, false));
    // wait for setActiveRoomData to happen, so we can be sure we're opening the right partis list
    yield take(setActiveRoomData);
  }

  yield put(fetchActiveParticipants(room_id));
  yield put(openRoomParticipantsList());
}

/**
 * Room management saga - handles room lifecycle and state management
 */
export function* roomManagementSaga() {
  // Set up callback to handle room channel reconnection after timeout
  const reconnectChannel = eventChannel(emitter => {
    realtimeConnections.setReconnectCallback(() => {
      emitter(roomChannelReconnected());
    });
    return () => {
      realtimeConnections.setReconnectCallback(null);
    };
  });

  // Fork a saga to listen for reconnect events
  yield fork(function* () {
    while (true) {
      const action = yield take(reconnectChannel);
      yield put(action);
    }
  });

  yield all([
    takeEvery(createCommunityChannel, handleCreateCommunityChannel),
    takeEvery(createRoom, handleCreateRoom),
    takeLatest(selectRoom, handleSelectRoom),
    takeLatest(selectOrCreateRoom, handleSelectOrCreateRoom),
    takeLatest(closeRoom, handleCloseRoom),
    takeEvery(reloadActiveRoom, handleReloadActiveRoom),
    takeEvery(roomChannelReconnected, handleRoomChannelReconnected),
    takeLatest(markRoomRead, handleMarkRoomReadSync),
    takeLatest(markAsRead, handleMarkAsRead),
    takeLatest(markAsUnread, handleMarkAsUnread),
    takeEvery(historyChangedDamz, handleHistoryChanged),
    takeLatest(fetchActiveParticipants, handleFetchActiveParticipants),
    takeLatest(addParticipants, handleAddParticipants),
    takeLatest(kickParticipant, handleKickParticipant),
    takeLatest(selectRoomAndOpenParticipantsList, handleSelectRoomAndOpenParticipantsList),
  ]);
}
